import type { InterestId, StrengthId, AmbitionId, LifePathId } from "../types";
import type { NarrativeContext } from "../narrative";

/**
 * Replaces every `{key}` token. Unknown keys are replaced with empty string.
 * Collapses resulting multi-spaces and trims leading/trailing spaces.
 */
export function fill(template: string, vars: Record<string, string>): string {
  const replaced = template.replace(/\{(\w+)\}/g, (_match, key: string) => {
    return Object.prototype.hasOwnProperty.call(vars, key) ? vars[key] : "";
  });
  return replaced.replace(/[ \t]{2,}/g, " ").trim();
}

/**
 * Pick one item from a readonly array using an rng returning [0, 1).
 */
export function pick<T>(arr: readonly T[], rng: () => number): T {
  if (arr.length === 0) {
    throw new Error("Cannot pick from empty array");
  }
  const idx = Math.floor(rng() * arr.length);
  return arr[Math.min(idx, arr.length - 1)];
}

/**
 * Pick n distinct items without repeats. Clamps n to arr.length.
 */
export function pickDistinct<T>(arr: readonly T[], n: number, rng: () => number): T[] {
  const pool = [...arr];
  const count = Math.max(0, Math.min(n, pool.length));
  const result: T[] = [];
  for (let i = 0; i < count; i++) {
    const idx = Math.floor(rng() * pool.length);
    result.push(pool.splice(idx, 1)[0]);
  }
  return result;
}

/**
 * Deterministic array shuffle using an rng returning [0, 1).
 */
export function shuffle<T>(arr: readonly T[], rng: () => number): T[] {
  const pool = [...arr];
  for (let i = pool.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    const temp = pool[i];
    pool[i] = pool[j];
    pool[j] = temp;
  }
  return pool;
}

/**
 * Pick an item from weighted items.
 */
export function pickWeighted<T>(
  items: readonly { value: T; weight: number }[],
  rng: () => number
): T {
  if (items.length === 0) {
    throw new Error("Cannot pick from empty weighted array");
  }
  const totalWeight = items.reduce((sum, it) => sum + Math.max(0, it.weight), 0);
  if (totalWeight <= 0) {
    return items[0].value;
  }
  let target = rng() * totalWeight;
  for (const item of items) {
    target -= Math.max(0, item.weight);
    if (target <= 0) {
      return item.value;
    }
  }
  return items[items.length - 1].value;
}

/**
 * Natural language list join:
 * [] -> ""
 * ["a"] -> "a"
 * ["a", "b"] -> "a and b"
 * ["a", "b", "c"] -> "a, b and c"
 */
export function listJoin(items: string[]): string {
  const filtered = items.filter((s) => s.trim().length > 0);
  if (filtered.length === 0) return "";
  if (filtered.length === 1) return filtered[0];
  if (filtered.length === 2) return `${filtered[0]} and ${filtered[1]}`;
  const head = filtered.slice(0, -1).join(", ");
  const tail = filtered[filtered.length - 1];
  return `${head} and ${tail}`;
}

export function cap(s: string): string {
  if (!s) return "";
  return s.charAt(0).toUpperCase() + s.slice(1);
}

export function lower(s: string): string {
  if (!s) return "";
  return s.toLowerCase();
}

export function ordinal(n: number): string {
  const rem100 = n % 100;
  if (rem100 >= 11 && rem100 <= 13) {
    return `${n}th`;
  }
  switch (n % 10) {
    case 1:
      return `${n}st`;
    case 2:
      return `${n}nd`;
    case 3:
      return `${n}rd`;
    default:
      return `${n}th`;
  }
}

export const INTEREST_PHRASE: Record<
  InterestId,
  { noun: string; field: string; verb: string; humanDomain: string }
> = {
  science: {
    noun: "the physical sciences",
    field: "natural philosophy and cosmology",
    verb: "measuring the world",
    humanDomain: "empirical inquiry and observation",
  },
  art: {
    noun: "visual art and aesthetics",
    field: "form, color, and tactile expression",
    verb: "rendering perceptions visible",
    humanDomain: "artistic craft and visual culture",
  },
  music: {
    noun: "sound and acoustic harmony",
    field: "frequency, rhythm, and vibrational structures",
    verb: "composing resonance",
    humanDomain: "music and auditory composition",
  },
  nature: {
    noun: "ecology and the living world",
    field: "biomes, organic cycles, and species co-evolution",
    verb: "tending wild systems",
    humanDomain: "ecological stewardship and earth science",
  },
  technology: {
    noun: "invention and engineering",
    field: "machinery, networks, and fabricated apparatuses",
    verb: "forging instruments",
    humanDomain: "toolmaking and mechanical design",
  },
  people: {
    noun: "society and culture",
    field: "human communities, language, and collective bonds",
    verb: "weaving civic ties",
    humanDomain: "social cohesion and communal dialogue",
  },
  stories: {
    noun: "narrative and lore",
    field: "oral memory, historical chronicle, and mythos",
    verb: "preserving memories",
    humanDomain: "literature, archival record, and storycraft",
  },
  adventure: {
    noun: "exploration and frontiers",
    field: "unmapped territory, navigational passes, and horizon crossings",
    verb: "breaching boundaries",
    humanDomain: "surveying frontiers and expeditionary navigation",
  },
};

export const STRENGTH_PHRASE: Record<
  StrengthId,
  { adj: string; noun: string; habit: string; asset: string }
> = {
  curiosity: {
    adj: "deeply inquisitive",
    noun: "persistent inquiry",
    habit: "probing unexamined assumptions",
    asset: "a hunger to question the boundaries of conventional truth",
  },
  empathy: {
    adj: "keenly attuned",
    noun: "emotional resonance",
    habit: "sensing the unspoken motives of others",
    asset: "a profound instinct for communal healing and shared grief",
  },
  discipline: {
    adj: "rigorously methodical",
    noun: "measured consistency",
    habit: "executing daily rituals with patient precision",
    asset: "an iron patience that outlasts fleeting distractions",
  },
  creativity: {
    adj: "inventively lateral",
    noun: "imaginative synthesis",
    habit: "joining unrelated principles into unexpected forms",
    asset: "an eye for hidden symmetry and novel solutions",
  },
  courage: {
    adj: "quietly steadfast",
    noun: "calm resolve",
    habit: "stepping forward when uncertainty paralyzed peers",
    asset: "an unshakeable moral spine when standing alone",
  },
  logic: {
    adj: "analytically lucid",
    noun: "axiomatic clarity",
    habit: "disentangling convoluted arguments step by step",
    asset: "the ability to trace complex causes through chaotic noise",
  },
  humor: {
    adj: "gently ironic",
    noun: "playful levity",
    habit: "puncturing pomposity with generous wit",
    asset: "an instinctive warmth that relieves catastrophic despair",
  },
  patience: {
    adj: "enduringly calm",
    noun: "long-horizon vision",
    habit: "waiting for intricate situations to ripen naturally",
    asset: "the composure to nurture slow-growth endeavors across years",
  },
};

export const AMBITION_PHRASE: Record<
  AmbitionId,
  { goal: string; drive: string; verbing: string; titlePhrase: string }
> = {
  understand: {
    goal: "understand fundamental reality",
    drive: "the hunger to grasp why the cosmos behaves as it does",
    verbing: "decoding deep principles",
    titlePhrase: "Seeker of First Principles",
  },
  build: {
    goal: "build enduring systems",
    drive: "the desire to construct frameworks and physical works that outlast a lifetime",
    verbing: "constructing generational foundations",
    titlePhrase: "Architect of Lasting Works",
  },
  help: {
    goal: "shelter and uplift others",
    drive: "the commitment to alleviate suffering and protect fragile lives",
    verbing: "extending shelter to vulnerable communities",
    titlePhrase: "Guardian of Fragile Lives",
  },
  lead: {
    goal: "chart common purpose",
    drive: "the resolve to unite disparate factions toward collective survival and renewal",
    verbing: "forging collective purpose",
    titlePhrase: "Catalyst of Common Will",
  },
  create: {
    goal: "bring novel beauty to life",
    drive: "the impulse to alter how conscious minds perceive existence through art and craft",
    verbing: "manifesting original beauty",
    titlePhrase: "Artisan of Radiant Form",
  },
  wander: {
    goal: "roam beyond the edge",
    drive: "the yearning to witness wonders that no existing chart has cataloged",
    verbing: "venturing past charted perimeters",
    titlePhrase: "Wayfarer of Outer Reaches",
  },
};

export const LIFE_PATH_PHRASE: Record<
  LifePathId,
  { role: string; manner: string; setting: string; identity: string }
> = {
  rooted: {
    role: "the rooted steward",
    manner: "tending generational soil and preserving local traditions",
    setting: "a close-knit township grounded by ancestral soil",
    identity: "a steadfast caretaker invested in place and community",
  },
  scholar: {
    role: "the archival scholar",
    manner: "immersing yourself in laboratory archives, treatises, and primary records",
    setting: "chambers of study, observatory terraces, and vast libraries",
    identity: "an investigator committed to rigorous evidence and continuous learning",
  },
  maker: {
    role: "the hands-on maker",
    manner: "working directly with raw material, prototypes, and practical craft",
    setting: "workbenches strewn with hand tools, shavings, and drafting paper",
    identity: "a builder whose understanding flows through tactile mastery",
  },
  voyager: {
    role: "the restless voyager",
    manner: "moving perpetually between horizons, waystations, and distant outposts",
    setting: "high passes, open waters, and migratory transit corridors",
    identity: "an itinerant explorer unwilling to remain long in one port",
  },
  guide: {
    role: "the compassionate guide",
    manner: "mentoring emerging minds and reconciling estranged companions",
    setting: "communal hearths, consultation clinics, and waystation courtyards",
    identity: "a counselor who measures accomplishment through the flourishing of others",
  },
  founder: {
    role: "the bold founder",
    manner: "establishing new guilds, research institutes, and expeditions from scratch",
    setting: "untested institutions and fledgling organizations",
    identity: "an initiator who transforms abstract possibilities into organized reality",
  },
};

/**
 * Returns 2-3 baseline "influence" sentences that every archetype prepends:
 * - which answers the visitor gave
 * - which were skipped and filled in by the generator
 * - whether the universe is shared or independent
 */
export function influenceLead(ctx: NarrativeContext): string[] {
  const { input, resolved, sharedUniverse, universe } = ctx;
  const sentences: string[] = [];

  // 1. Interests & Strengths statement
  const chosenInterests = (input?.interests?.length ?? 0) > 0;
  const chosenStrengths = (input?.strengths?.length ?? 0) > 0;

  if (chosenInterests && chosenStrengths) {
    const intList = listJoin((input?.interests ?? []).map((id) => INTEREST_PHRASE[id]?.noun ?? id));
    const strList = listJoin((input?.strengths ?? []).map((id) => STRENGTH_PHRASE[id]?.noun ?? id));
    sentences.push(
      `Your chosen focus on ${intList} paired with ${strList} anchored the trade and temperament of this self.`
    );
  } else if (chosenInterests) {
    const intList = listJoin((input?.interests ?? []).map((id) => INTEREST_PHRASE[id]?.noun ?? id));
    const filledStr = listJoin((resolved?.strengths ?? []).map((id) => STRENGTH_PHRASE[id]?.noun ?? id));
    sentences.push(
      `You selected ${intList} as your primary pursuit, and because you skipped the strength questions, the generator selected ${filledStr} as the guiding discipline for this timeline.`
    );
  } else if (chosenStrengths) {
    const strList = listJoin((input?.strengths ?? []).map((id) => STRENGTH_PHRASE[id]?.noun ?? id));
    const filledInt = listJoin((resolved?.interests ?? []).map((id) => INTEREST_PHRASE[id]?.noun ?? id));
    sentences.push(
      `Your declared strength of ${strList} directed this self's moral spine, while the generator supplied an affinity for ${filledInt} because no interests were checked.`
    );
  } else {
    const filledInt = listJoin((resolved?.interests ?? []).map((id) => INTEREST_PHRASE[id]?.noun ?? id));
    const filledStr = listJoin((resolved?.strengths ?? []).map((id) => STRENGTH_PHRASE[id]?.noun ?? id));
    sentences.push(
      `You skipped both interests and strengths, so the generator chose a foundational curiosity toward ${filledInt} and a temper of ${filledStr} for this manifestation.`
    );
  }

  // 2. Ambition & Life Path statement
  const ambitionGoal = AMBITION_PHRASE[resolved?.ambition]?.goal ?? "seek purpose";
  const lifeRole = LIFE_PATH_PHRASE[resolved?.lifePath]?.role ?? "the traveler";

  if (resolved?.filled?.ambition && resolved?.filled?.lifePath) {
    sentences.push(
      `Because you omitted both ambition and life path, the generator assigned the ambition to "${ambitionGoal}" along the cadence of "${lifeRole}".`
    );
  } else if (resolved?.filled?.ambition) {
    sentences.push(
      `You chose the path of "${lifeRole}", while the generator filled the unselected ambition with a drive to "${ambitionGoal}".`
    );
  } else if (resolved?.filled?.lifePath) {
    sentences.push(
      `Your ambition to "${ambitionGoal}" drove this life, and because life path was left open, the generator channeled it through the style of "${lifeRole}".`
    );
  } else {
    sentences.push(
      `Your declared ambition to "${ambitionGoal}" merged directly with your chosen walk as "${lifeRole}", dictating the scope of this persona's career.`
    );
  }

  // 3. Universe context statement
  if (sharedUniverse) {
    sentences.push(
      `All four timelines in this reading share the exact physical laws of "${universe?.name ?? "Cosmos"}" (${universe?.code ?? "U"}), meaning this self confronts the same ${(universe?.profile?.habitabilityLabel ?? "Barren Void").toLowerCase()} cosmology as the other three branches.`
    );
  } else {
    const dims = universe?.config?.dimensions ?? 3;
    const grav = typeof universe?.config?.gravity === "number" ? universe.config.gravity.toFixed(2) : "1.00";
    sentences.push(
      `This timeline exists within an independently calibrated cosmos ("${universe?.name ?? "Cosmos"}"), where ${dims} spatial dimensions and ${grav}× gravity framed the physical possibilities of this self's world.`
    );
  }

  return sentences;
}
